import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "@/features/auth/store/sessionSlice";
import { FeatureTeasingService as api } from "@/shared/services/featureTeasing.service";
import type { CampaignOpening } from "@/shared/types/featureTeasing.types";
import { CampaignVisitContext } from "./campaignVisitContext";

type Props = { ready?: boolean };
const Visit = ({ ready, organizationId }: Props & { organizationId: string }) => {
  const visit = useContext(CampaignVisitContext);
  useEffect(() => {
    if (!ready || !visit || visit.consumed) return;
    const controller = new AbortController();
    let active = true;
    let timer: number;
    let waits = 0;
    let nextAttempts = 0;
    // Retry budgets belong to this dashboard mount, not the whole visit.
    let openAttempts = 0;
    let renewals = 0;
    const hasDialog = () => Boolean(document.querySelector('[role="dialog"], [role="alertdialog"]'));
    const transient = (error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      return status === undefined || status === 408 || status === 429 || status >= 500;
    };
    const schedule = (operation: () => void) => {
      if (active) timer = window.setTimeout(operation, 800);
    };
    const present = (result: CampaignOpening) => {
      if (!active || visit.consumed) return;
      // A deferred modal must not outlive the server's reservation lease.
      if (result.expires_at && Date.parse(result.expires_at) <= Date.now()) {
        visit.reservation = undefined;
        // Only an established expiry permits a fresh UUID and eligibility check.
        // Bound renewal even if the server repeatedly returns expired leases.
        if (renewals++ < 1 && nextAttempts < 2 && openAttempts < 2) schedule(() => void attempt());
        return;
      }
      if (hasDialog()) {
        if (++waits <= 12) schedule(() => present(result));
        return;
      }
      visit.present(result);
    };
    const attempt = async () => {
      if (!active || visit.consumed) return;
      if (hasDialog()) {
        if (++waits <= 12) schedule(() => void attempt());
        return;
      }
      try {
        if (!visit.reservation) {
          nextAttempts++;
          const decision = await api.next(organizationId, controller.signal);
          if (!active || !decision.campaign || visit.consumed) return;
          visit.reservation = { campaignId: decision.campaign.id, uuid: crypto.randomUUID() };
        }
        const reservation = visit.reservation;
        if (!reservation.pending) {
          if (openAttempts >= 2) return;
          openAttempts++;
          // Retain the same UUID and in-flight request across route changes.
          reservation.pending = api.open(organizationId, reservation.campaignId, reservation.uuid);
        }
        present(await reservation.pending);
      }
      catch (error) {
        if (!active) return;
        const reservation = visit.reservation;
        if (!transient(error)) return;
        if (reservation) reservation.pending = undefined;
        if ((reservation ? openAttempts : nextAttempts) < 2) schedule(() => void attempt());
      }
    };
    schedule(() => void attempt());
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [organizationId, ready, visit]);
  return null;
};

export const CampaignTeaserVisit = ({ ready = true }: Props) => {
  const representative = useSelector(selectUser)?.representative;
  if (!representative?.organization_id || !representative.id) return null;
  return <Visit key={`${representative.organization_id}:${representative.id}`} organizationId={representative.organization_id} ready={ready} />;
};
